import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { Input } from '@shared/components/ui/shadcn/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/shadcn/radio-group';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/shadcn/select';
import { ArrowLeft, MapPin, Package, Plus, Trash2 } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PendingDispatchLocation, DispatchByLocationLine } from '../../models';

export interface DispatchModalRef {
  open: (
    locations: PendingDispatchLocation[],
    onConfirm: (
      locationId: number,
      dispatchType: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER',
      lines?: DispatchByLocationLine[],
    ) => Promise<void>,
    orderId: number,
  ) => void;
}

type DispatchStep = 'select_type' | 'configure_groups';

export const DispatchModal = forwardRef<DispatchModalRef>((_, ref) => {
  const { t } = useTranslation('extraWorkOrdersModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [locations, setLocations] = useState<PendingDispatchLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [dispatchType, setDispatchType] = useState<'LOCATION_ORDER' | 'EXTRA_WORK_ORDER'>('LOCATION_ORDER');
  const [currentStep, setCurrentStep] = useState<DispatchStep>('select_type');
  const [isProcessing, setIsProcessing] = useState(false);
  const [lines, setLines] = useState<DispatchByLocationLine[]>([]);
  const confirmCallbackRef = useRef<
    | ((
        locationId: number,
        dispatchType: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER',
        lines?: DispatchByLocationLine[],
      ) => Promise<void>)
    | null
  >(null);

  useImperativeHandle(ref, () => ({
    open: (
      locationsData: PendingDispatchLocation[],
      onConfirm: (
        locationId: number,
        dispatchType: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER',
        lines?: DispatchByLocationLine[],
      ) => Promise<void>,
      _orderId: number,
    ) => {
      setLocations(locationsData);
      setSelectedLocationId(locationsData.length > 0 ? locationsData[0].location_id : null);
      setDispatchType('LOCATION_ORDER');
      setCurrentStep('select_type');
      setIsProcessing(false);
      setLines([]);
      confirmCallbackRef.current = onConfirm;
      modalRef.current?.openModal();
    },
  }));

  const buildImageUrl = (imageUrl?: string | null): string => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageUrl}`;
  };

  const handleDispatchTypeChange = (value: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER') => {
    setDispatchType(value);
    if (value === 'LOCATION_ORDER') {
      setCurrentStep('select_type');
    }
  };

  const handleNextStep = () => {
    if (dispatchType === 'EXTRA_WORK_ORDER') {
      setCurrentStep('configure_groups');
    } else {
      handleConfirm();
    }
  };

  const handleBackToSelectType = () => {
    setCurrentStep('select_type');
  };

  const generateGroupCode = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, '0');
    return `TE-${year}${month}-${random}`;
  };

  const updateLine = (index: number, field: keyof DispatchByLocationLine, value: any) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setLines(newLines);
  };

  const addLineForProduct = (productId: number, totalAvailable: number) => {
    const assignedQty = lines.filter((l) => l.product_id === productId).reduce((sum, l) => sum + l.qty_expected, 0);

    const availableQty = totalAvailable - assignedQty;
    if (availableQty <= 0) return;

    const newLine: DispatchByLocationLine = {
      group_code: generateGroupCode(),
      product_id: productId,
      qty_expected: availableQty,
      work_type: 'LABELING',
    };

    setLines([...lines, newLine]);
  };

  const removeLine = (index: number) => {
    const newLines = lines.filter((_, i) => i !== index);
    setLines(newLines);
  };

  const areAllUnitsAssigned = (): boolean => {
    if (dispatchType !== 'EXTRA_WORK_ORDER') return true;
    if (lines.length === 0) return false;

    const selectedLocation = locations.find((loc) => loc.location_id === selectedLocationId);
    if (!selectedLocation) return false;

    for (const product of selectedLocation.products) {
      const assigned = lines
        .filter((l) => l.product_id === product.product_id)
        .reduce((sum, l) => sum + l.qty_expected, 0);
      if (assigned !== product.qty) return false;
    }

    return lines.every((l) => l.group_code?.trim() !== '');
  };

  const handleConfirm = async () => {
    if (!selectedLocationId || !confirmCallbackRef.current) return;

    setIsProcessing(true);
    try {
      await confirmCallbackRef.current(
        selectedLocationId,
        dispatchType,
        dispatchType === 'EXTRA_WORK_ORDER' ? lines : undefined,
      );
      modalRef.current?.closeModal();
    } catch (error) {
      console.error('Error al despachar:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const selectedLocation = locations.find((loc) => loc.location_id === selectedLocationId);

  return (
    <ModalCenter
      ref={modalRef}
      title={t('detail.dispatch.modal.title', 'Despachar Ubicación')}
      type="info"
      width="xl"
      onClose={() => {}}
    >
      <div className="space-y-6">
        {currentStep === 'select_type' && (
          <>
            {locations.length > 1 && (
              <div className="space-y-2">
                <Label>{t('detail.dispatch.modal.select_location', 'Seleccionar Ubicación')} *</Label>
                <Select
                  value={selectedLocationId?.toString() || ''}
                  onValueChange={(v) => setSelectedLocationId(parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('detail.dispatch.modal.location_placeholder', 'Seleccionar ubicación...')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.location_id} value={loc.location_id.toString()}>
                        {loc.location_code} ({loc.pending_movements}{' '}
                        {t('detail.dispatch.modal.movements', 'movimientos')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-3">
              <Label>{t('detail.dispatch.modal.dispatch_type', 'Tipo de Despacho')} *</Label>
              <RadioGroup
                value={dispatchType}
                onValueChange={(v) => handleDispatchTypeChange(v as any)}
                className="grid grid-cols-2 gap-3"
              >
                <label
                  className={`relative flex flex-col p-4 border rounded-lg cursor-pointer transition-all ${dispatchType === 'LOCATION_ORDER' ? 'border-primary/30 bg-muted/50' : 'border-border bg-background'}`}
                >
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="LOCATION_ORDER" id="lo" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-primary" />
                        <span className="font-semibold">
                          {t('detail.dispatch.modal.location_order', 'Orden de Ubicación')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t('detail.dispatch.modal.location_order_description')}
                      </p>
                    </div>
                  </div>
                </label>

                <label
                  className={`relative flex flex-col p-4 border rounded-lg cursor-pointer transition-all ${dispatchType === 'EXTRA_WORK_ORDER' ? 'border-primary/30 bg-muted/50' : 'border-border bg-background'}`}
                >
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="EXTRA_WORK_ORDER" id="ew" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="w-4 h-4 text-primary" />
                        <span className="font-semibold">
                          {t('detail.dispatch.modal.extra_work_order', 'Orden de Trabajo Extra')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t('detail.dispatch.modal.extra_work_order_description')}
                      </p>
                    </div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {selectedLocation && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  {t('detail.dispatch.modal.products', 'Productos')} ({selectedLocation.products.length})
                </Label>
                <ScrollArea className="max-h-48">
                  <div className="space-y-2 pr-2">
                    {selectedLocation.products.map((p) => (
                      <div
                        key={p.product_id}
                        className="flex items-center justify-between p-3 bg-background rounded-lg border border-border"
                      >
                        <Product
                          id={p.product_id}
                          name={p.product_name}
                          src={buildImageUrl(p.product_image)}
                          code={p.product_code}
                        />
                        <span className="text-sm font-semibold ml-4">{p.qty} ud.</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </>
        )}

        {currentStep === 'configure_groups' && selectedLocation && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('detail.dispatch.modal.configure_groups_description')}</p>
            <div className="max-h-[60vh] overflow-y-auto space-y-6 pr-2">
              {selectedLocation.products.map((p) => {
                const prodLines = lines.filter((l) => l.product_id === p.product_id);
                const totalAssigned = prodLines.reduce((s, l) => s + l.qty_expected, 0);

                return (
                  <div key={p.product_id} className="border border-border rounded-lg p-5 bg-background">
                    <div className="flex items-center justify-between mb-4">
                      <Product
                        id={p.product_id}
                        name={p.product_name}
                        src={buildImageUrl(p.product_image)}
                        code={p.product_code}
                      />
                      {totalAssigned < p.qty && (
                        <Button
                          onClick={() => addLineForProduct(p.product_id, p.qty)}
                          variant="primary"
                          size="sm"
                          className="gap-2"
                        >
                          <Plus className="w-4 h-4" /> {t('detail.dispatch.modal.add_group', 'Añadir')}
                        </Button>
                      )}
                    </div>
                    <div className="space-y-3">
                      {prodLines.map((line, idx) => {
                        const globalIdx = lines.indexOf(line);
                        return (
                          <div
                            key={idx}
                            className="bg-muted/30 p-4 rounded-lg flex gap-4 items-end border border-border/50"
                          >
                            <div className="flex-1 space-y-1.5">
                              <Label className="text-xs text-muted-foreground">
                                {t('detail.dispatch.modal.group_code')}
                              </Label>
                              <Input
                                value={line.group_code}
                                onChange={(e) => updateLine(globalIdx, 'group_code', e.target.value)}
                                className="h-9"
                              />
                            </div>
                            <div className="w-24 space-y-1.5">
                              <Label className="text-xs text-muted-foreground">
                                {t('detail.dispatch.modal.quantity')}
                              </Label>
                              <Input
                                type="number"
                                value={line.qty_expected}
                                onChange={(e) => updateLine(globalIdx, 'qty_expected', parseInt(e.target.value))}
                                className="h-9"
                              />
                            </div>
                            <div className="w-9">
                              <Button
                                onClick={() => removeLine(globalIdx)}
                                variant="outline"
                                size="sm"
                                className="h-9 w-9 p-0 text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-4 border-t border-border">
          <Button
            variant="outline"
            className="flex-1"
            onClick={currentStep === 'configure_groups' ? handleBackToSelectType : () => modalRef.current?.closeModal()}
          >
            {currentStep === 'configure_groups' ? t('detail.dispatch.modal.back') : t('detail.dispatch.modal.cancel')}
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            disabled={isProcessing || (currentStep === 'configure_groups' && !areAllUnitsAssigned())}
            onClick={currentStep === 'select_type' ? handleNextStep : handleConfirm}
          >
            {isProcessing
              ? t('detail.dispatch.modal.processing')
              : currentStep === 'select_type' && dispatchType === 'EXTRA_WORK_ORDER'
                ? t('detail.dispatch.modal.next')
                : t('detail.dispatch.modal.confirm')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

DispatchModal.displayName = 'DispatchModal';
